import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  X,
  ShoppingCart,
  ConciergeBell,
  Bed,
  Users,
  Car,
  Clock,
  QrCode,
  UtensilsCrossed,
  CalendarDays,
  User,
} from "lucide-react";
import { Input } from "../ui/input";
import { useGlobalSearchQuery } from "../../hooks/queries/useGlobalSearchQuery";
import { cn } from "../../lib/utils";
import { useDebounce } from "../../hooks/useDebounce";

interface GlobalSearchProps {
  placeholder?: string;
  debounceMs?: number;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  placeholder = "Search orders, rooms, guests...",
  debounceMs = 300,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const debouncedQuery = useDebounce(query, debounceMs);

  // Search query with React Query
  const { data: searchResults, isLoading } = useGlobalSearchQuery(
    debouncedQuery,
    {
      enabled: debouncedQuery.length >= 2,
    },
  );

  // Icon mapping for different entity types
  const getIcon = (icon: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      "shopping-cart": <ShoppingCart className="h-4 w-4" />,
      "concierge-bell": <ConciergeBell className="h-4 w-4" />,
      bed: <Bed className="h-4 w-4" />,
      users: <Users className="h-4 w-4" />,
      car: <Car className="h-4 w-4" />,
      clock: <Clock className="h-4 w-4" />,
      "qr-code": <QrCode className="h-4 w-4" />,
      utensils: <UtensilsCrossed className="h-4 w-4" />,
      calendar: <CalendarDays className="h-4 w-4" />,
      user: <User className="h-4 w-4" />,
    };
    return iconMap[icon] || <Search className="h-4 w-4" />;
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || !searchResults?.results) return;

      const totalResults = searchResults.results.length;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % totalResults);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + totalResults) % totalResults);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const selected = searchResults.results[selectedIndex];
        if (selected) {
          navigate(selected.navigationUrl);
          setIsOpen(false);
          setQuery("");
        }
      } else if (e.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, searchResults, selectedIndex, navigate]);

  // Global shortcut: Ctrl+/
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "/") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Open dropdown when typing
  useEffect(() => {
    if (debouncedQuery.length >= 2 && searchResults) {
      setIsOpen(true);
      setSelectedIndex(0);
    } else {
      setIsOpen(false);
    }
  }, [debouncedQuery, searchResults]);

  return (
    <div className="relative w-full max-w-md" ref={dropdownRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (query.length >= 2 && searchResults) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          className="pl-10 pr-10"
        />
        {query && (
          <button
            onClick={() => {
              setQuery("");
              setIsOpen(false);
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
          >
            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && searchResults && searchResults.results.length > 0 && (
        <div className="absolute mt-2 w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 overflow-hidden z-50">
          <div className="max-h-96 overflow-y-auto">
            {searchResults.results.map((item, index) => (
              <button
                key={`${item.entityType}-${item.entityId}`}
                onClick={() => {
                  const targetUrl = new URL(
                    item.navigationUrl,
                    window.location.origin,
                  );
                  const currentUrl = new URL(window.location.href);

                  // If we're navigating to the same path, force a reload
                  if (targetUrl.pathname === currentUrl.pathname) {
                    // Use window.location to force a full navigation
                    window.location.href = item.navigationUrl;
                  } else {
                    navigate(item.navigationUrl);
                  }

                  setIsOpen(false);
                  setQuery("");
                }}
                className={cn(
                  "w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors",
                  selectedIndex === index && "bg-blue-50 dark:bg-blue-900/20",
                )}
              >
                {/* Icon */}
                <div className="shrink-0 mt-0.5 text-gray-400 dark:text-gray-500">
                  {getIcon(item.icon)}
                </div>

                {/* Content */}
                <div className="flex-1 text-left">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {item.title}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {item.subtitle}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {item.sourceScreen}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Search info footer */}
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-t dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
            Found {searchResults.totalMatches} results •{" "}
            {searchResults.searchTimeMs}ms
          </div>
        </div>
      )}

      {/* Loading state */}
      {isOpen && isLoading && (
        <div className="absolute mt-2 w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 p-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        </div>
      )}

      {/* No results */}
      {isOpen && searchResults && searchResults.results.length === 0 && (
        <div className="absolute mt-2 w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 p-4">
          <div className="text-center text-gray-500 dark:text-gray-400">
            No results found for "{query}"
          </div>
        </div>
      )}
    </div>
  );
};
